/**
 * useDocumentDeletionReturn — a decisão de devolver estoque ao EXCLUIR.
 *
 * A pergunta do modo `ask` mora dentro do diálogo de confirmação da exclusão,
 * não num cartão separado: depois que o documento morre o vínculo
 * `stock_movements.quote_id` vira NULL (ver `src/lib/stockDocumentReturn.ts`) e
 * não existe segunda chance de perguntar. Um convite pós-exclusão não teria o
 * que oferecer.
 *
 * A sobreposição POR PRODUTO (`products.stock_deduction_mode`) não é consultada
 * aqui de propósito: a devolução é derivada dos movimentos que existem, e um
 * produto configurado como `off` simplesmente nunca gerou movimento — logo não
 * aparece na conta. Consultar o modo do produto aqui só criaria a chance de
 * deixar mercadoria baixada para sempre.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useFeature } from '@/hooks/useFeature';
import { useRole } from '@/hooks/useRole';
import { useStockSettings } from '@/hooks/useStockSettings';
import {
  fetchDocumentReturnLines,
  prepareDocumentStockReturn,
  type DocumentKind,
  type PreparedDocumentReturn,
} from '@/lib/stockDocumentReturn';

export interface DocumentDeletionReturn {
  /** 'off' | 'ask' | 'auto', já resolvido pela configuração da empresa. */
  action: 'off' | 'ask' | 'auto';
  /** Linhas de produto que voltariam ao estoque (0 = nada a oferecer). */
  productCount: number;
  /**
   * Quantos DOCUMENTOS têm algo a devolver — quase sempre 1, mas a exclusão em
   * massa pode selecionar vários. Sem isto o aviso dizia "neste documento" com
   * 3 orçamentos selecionados (visto em device; primo do #87, contagem na
   * string). Note que não é o total de selecionados: um orçamento sem baixa
   * prévia não entra na conta, e prometer devolução dele seria mentira.
   */
  documentCount: number;
  /** Só no modo `ask`: o usuário quer devolver? */
  shouldReturn: boolean;
  setShouldReturn: (value: boolean) => void;
  /** O que vai acontecer de fato ao confirmar a exclusão (online). */
  willReturn: boolean;
  /**
   * A DECISÃO, sem depender de ter conseguido contar as linhas.
   *
   * É este valor que viaja na fila offline: sem rede a consulta de movimentos
   * não roda e `productCount` é 0, então usar `willReturn` enfileiraria sempre
   * "não devolver" — o gatilho seria inerte justamente no cenário de campo.
   */
  intent: boolean;
  /**
   * CALCULA o que devolver e entrega o aplicador. **Chame antes do delete e
   * aplique depois** — quem faz esse encadeamento é `runDocumentDeletion`
   * (`src/lib/documentDeletion.ts`), que existe justamente para a ordem não ser
   * recopiada errada. Devolve `null` quando não há nada a fazer, para o
   * chamador não anunciar uma devolução que não houve.
   */
  prepare: () => Promise<PreparedDocumentReturn | null>;
}

export function useDocumentDeletionReturn(
  kind: DocumentKind,
  ids: readonly string[],
  options: { enabled?: boolean } = {},
): DocumentDeletionReturn {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const inventory = useFeature('inventory');
  const { canWrite } = useRole();
  const { actionFor } = useStockSettings();

  const action = actionFor('document_deleted_return');
  const companyId = activeCompany?.id ?? null;

  // Ids reais e estáveis: um `offline_` nunca teve movimento no servidor, e a
  // ordem da seleção não pode refazer a consulta.
  const targets = useMemo(
    () =>
      Array.from(
        new Set(
          (ids ?? [])
            .map((id) => String(id ?? ''))
            .filter((id) => id.length > 0 && !id.startsWith('offline_')),
        ),
      ).sort(),
    [ids],
  );

  const enabled =
    options.enabled !== false &&
    Boolean(user?.id && companyId) &&
    inventory.allowed &&
    canWrite &&
    action !== 'off' &&
    targets.length > 0;

  const { data } = useQuery({
    queryKey: ['stock-deletion-return', kind, companyId, targets.join(',')],
    enabled,
    networkMode: 'offlineFirst',
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
    queryFn: () => fetchDocumentReturnLines(companyId!, kind, targets),
  });

  const productCount = useMemo(() => {
    let total = 0;
    for (const lines of data?.values() ?? []) total += lines.length;
    return total;
  }, [data]);

  const documentCount = data?.size ?? 0;

  // Pré-marcado: quem ligou o gatilho já disse que quer devolver; o modo `ask`
  // existe para poder NÃO devolver num caso específico (a mercadoria sumiu, o
  // documento estava errado), não para pedir permissão toda vez.
  const [shouldReturn, setShouldReturn] = useState(true);

  const intent = action === 'auto' ? true : action === 'ask' && shouldReturn;
  const willReturn = intent && productCount > 0;

  const prepare = async (): Promise<PreparedDocumentReturn | null> => {
    if (!willReturn || !companyId) return null;
    // Relê os movimentos em vez de reaproveitar o `data` da query: entre a
    // abertura do diálogo e a confirmação o saldo pode ter mudado (outro
    // device, outra aba, o sync offline).
    const prepared = await prepareDocumentStockReturn(companyId, kind, targets);
    return prepared.products > 0 ? prepared : null;
  };

  return {
    action,
    productCount,
    documentCount,
    shouldReturn,
    setShouldReturn,
    willReturn,
    intent,
    prepare,
  };
}
